import { useEffect, useState } from "react";
import { apiInstanceFetch } from "../../../util/api";
import { setToast } from "../../../util/toast";
import Pagination from "../../extra/Pagination";
import dayjs from "dayjs";
import defaultImage from "../../../assets/images/default.jpg";

const FaithLeaderRequest = () => {
  const [status, setStatus] = useState("pending");
  const [applications, setApplications] = useState([]);
  const [total, setTotal] = useState(0);
  const [start, setStart] = useState(1);
  const [limit, setLimit] = useState(500); // fetch all — table is small, search must cover everything
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [proofUrl, setProofUrl] = useState(null);

  const fetchData = () => {
    setLoading(true);
    apiInstanceFetch
      .get(`faithLeader/adminList?status=${status}&start=${start}&limit=${limit}`)
      .then((res) => {
        if (res?.status) {
          setApplications(res.applications || []);
          setTotal(res.total || 0);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [status, start, limit]);

  const handleDecision = (applicationId, decision, reason) => {
    apiInstanceFetch
      .post(`faithLeader/adminDecision`, {
        applicationId,
        decision,
        reason: reason || "",
      })
      .then((res) => {
        if (res?.status) {
          setToast("success", res.message || "Decision saved.");
          setApplications((prev) => prev.filter((a) => a._id !== applicationId));
        } else {
          setToast("error", res?.message || "Decision failed.");
        }
      })
      .catch((err) => {
        console.error(err);
        setToast("error", "Decision failed.");
      });
  };

  const startReject = (applicationId) => {
    setRejectFor(applicationId);
    setRejectReason("");
  };

  const confirmReject = () => {
    if (!rejectFor) return;
    handleDecision(rejectFor, "reject", rejectReason.trim());
    setRejectFor(null);
    setRejectReason("");
  };

  const handleChangePage = (event, newPage) => {
    setStart(newPage + 1);
  };

  const handleChangeRowsPerPage = (value) => {
    setLimit(parseInt(value, 10));
    setStart(1);
  };

  const handleViewProof = (url) => {
    if (!url) {
      setToast("error", "No proof document uploaded.");
      return;
    }
    setProofUrl(url);
  };

  // Hard delete — removes the application and revokes leader status if it
  // had been approved.
  const handleDelete = (app) => {
    const name = app?.fullName || app?.userId?.firstName || "this leader";
    if (!window.confirm(`Delete ${name}? This removes the application${app.status === "approved" ? " AND revokes their faith-leader access" : ""}. This cannot be undone.`)) return;
    apiInstanceFetch
      .delete(`faithLeader/admin/${app._id}`)
      .then((res) => {
        if (res?.status) {
          setToast("success", res.message || "Deleted.");
          setApplications((prev) => prev.filter((a) => a._id !== app._id));
        } else {
          setToast("error", res?.message || "Delete failed.");
        }
      })
      .catch((err) => {
        console.error(err);
        setToast("error", "Delete failed.");
      });
  };

  // Client-side search over the loaded rows (the list fetches everything —
  // the table is small).
  const visibleApplications = search.trim()
    ? applications.filter((a) => {
        const hay = [
          a?.fullName,
          a?.organisationName,
          a?.religion,
          a?.denomination,
          a?.contactEmail,
          a?.contactPhone,
          a?.userId?.firstName,
          a?.userId?.lastName,
          a?.userId?.email,
          a?.userId?.uniqueId,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(search.trim().toLowerCase());
      })
    : applications;

  const handleCloseProof = () => setProofUrl(null);

  return (
    <div className="mainFaithLeader">
      <div className="userMain">
        <div className="tableMain mt-3">
          <div className="d-flex justify-content-between align-items-center px-3 pt-2 pb-2 flex-wrap">
            <div>
              <h4>Faith Leader Applications</h4>
              <small className="text-secondary">Verify and approve religious leaders.</small>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, org, email…"
                className="form-control form-control-sm"
                style={{ minWidth: 220 }}
              />
              {["pending", "approved", "rejected"].map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`btn btn-sm ${status === s ? "btn-primary" : "btn-outline-secondary"} text-capitalize`}
                  onClick={() => {
                    setStatus(s);
                    setStart(1);
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="px-3">
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User</th>
                    <th>Full Name</th>
                    <th>Organisation</th>
                    <th>Tradition</th>
                    <th>Denomination</th>
                    <th>Contact</th>
                    <th>Proof</th>
                    <th>Submitted</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="10" className="text-center text-secondary py-4">
                        Loading…
                      </td>
                    </tr>
                  ) : visibleApplications.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="text-center text-secondary py-4">
                        No {status} applications.
                      </td>
                    </tr>
                  ) : (
                    visibleApplications.map((a, idx) => {
                      const user = a.userId || {};
                      return (
                        <tr key={a._id}>
                          <td>{(start - 1) * limit + idx + 1}</td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <img
                                src={user.image || defaultImage}
                                alt=""
                                style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }}
                                onError={(e) => (e.currentTarget.src = defaultImage)}
                              />
                              <div>
                                <div>{user.firstName || ""} {user.lastName || ""}</div>
                                <small className="text-secondary">{user.uniqueId || ""}</small>
                              </div>
                            </div>
                          </td>
                          <td>{a.fullName}</td>
                          <td>{a.organisationName}</td>
                          <td className="text-capitalize">{a.religion}</td>
                          <td>{a.denomination || "—"}</td>
                          <td>
                            <div>{a.contactPhone || "—"}</div>
                            <small className="text-secondary">{a.contactEmail || ""}</small>
                          </td>
                          <td>
                            {a.proofDocument ? (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-info"
                                onClick={() => handleViewProof(a.proofDocument)}
                              >
                                View
                              </button>
                            ) : (
                              <span className="text-secondary">—</span>
                            )}
                          </td>
                          <td>{dayjs(a.createdAt).format("DD MMM YYYY, HH:mm")}</td>
                          <td>
                            {status === "pending" ? (
                              <div className="d-flex gap-1">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-success"
                                  onClick={() => handleDecision(a._id, "approve")}
                                >
                                  Approve
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger"
                                  onClick={() => startReject(a._id)}
                                >
                                  Reject
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDelete(a)}
                                >
                                  Delete
                                </button>
                              </div>
                            ) : status === "rejected" ? (
                              <div className="d-flex gap-1 align-items-center">
                                <small className="text-secondary">
                                  {a.rejectionReason || "No reason"}
                                </small>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDelete(a)}
                                >
                                  Delete
                                </button>
                              </div>
                            ) : (
                              <div className="d-flex gap-1 align-items-center">
                                <span className="badge bg-success">Approved</span>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDelete(a)}
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              componentName={"Faith Leaders"}
              totalData={total}
              page={start - 1}
              rowsPerPage={limit}
              setPage={handleChangePage}
              handleChangeRowPerPage={handleChangeRowsPerPage}
            />
          </div>
        </div>
      </div>

      {/* Reject reason modal */}
      {rejectFor && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ background: "rgba(0,0,0,0.6)" }}
          onClick={() => setRejectFor(null)}
        >
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Reject application</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setRejectFor(null)}
                ></button>
              </div>
              <div className="modal-body">
                <label className="form-label">Reason (optional)</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Will be sent to the applicant."
                ></textarea>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setRejectFor(null)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmReject}>
                  Confirm reject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Proof viewer */}
      {proofUrl && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ background: "rgba(0,0,0,0.85)" }}
          onClick={handleCloseProof}
        >
          <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Proof of leadership</h5>
                <button type="button" className="btn-close" onClick={handleCloseProof}></button>
              </div>
              <div className="modal-body text-center">
                {proofUrl.toLowerCase().endsWith(".pdf") ? (
                  <iframe
                    src={proofUrl}
                    title="proof"
                    style={{ width: "100%", height: 600, border: 0, background: "#fff" }}
                  ></iframe>
                ) : (
                  <img src={proofUrl} alt="proof" style={{ maxWidth: "100%", maxHeight: 600 }} />
                )}
              </div>
              <div className="modal-footer">
                <a href={proofUrl} target="_blank" rel="noreferrer" className="btn btn-outline-light">
                  Open in new tab
                </a>
                <button type="button" className="btn btn-secondary" onClick={handleCloseProof}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FaithLeaderRequest;
